import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import FormDrawer from "components/__new/common/form-drawer";
import moment from "moment";
import { ButtonLoader, Input, SelectInput } from "../common/form";
import numberOrdinalSuffix from "utils/numberOrdinalSuffix";
import { weekdays } from "utils/dateFormat";
import * as payrollSetupAction from "store/entities/payroll/payrollSetup/action";
import successGif from "assets/images/icons/success-green.gif";
import Maybe from "components/__new/common/Maybe";
import { REGEX_VALUES } from "utils/regexValues";


const PayScheduleForm = (props) => {
    const { isOpen, setIsOpen, paySchedule, getPaySchedules } = props;
    const formAction = Object.keys(paySchedule).length === 0 ? "create" : "edit";
    
    const dispatch = useDispatch();
    const { payScheduleFormActionLoading, payScheduleFormAction } = useSelector(state => state.entities.payrollSetup)
    
    const formDefault = {
        name: "",
        frequency: "",
        workday_proration: false,
        
        day_of_month: "",
        
        first_run_percent: "",
        first_run_day: "",
        second_run_percent: "",
        second_run_day: "",
        
        day_of_week: "",
        start_of_pay_period: "",
    }
    const [form, setForm] = useState(formDefault)
    const [isSuccess, setIsSuccess] = useState(false);
    
    const submitForm = async (e) => {
        e.preventDefault();
        let frequency = {};
        let params = {
            name: form.name,
            workday_proration: form.workday_proration,
        }
        
        if (form.frequency === "MONTHLY"){
            frequency = {
                day_of_month: parseInt(form.day_of_month),
            }
        }
        else if (form.frequency === "BI_MONTHLY"){
            frequency = {
                first_run_percent: parseInt(form.first_run_percent),
                first_run_day: parseInt(form.first_run_day),
                second_run_percent: parseInt(form.second_run_percent),
                second_run_day: parseInt(form.second_run_day),
            }
        }
        else if (form.frequency === "WEEKLY"){
            frequency = {
                day_of_week: parseInt(form.day_of_week),
                start_of_pay_period: form.start_of_pay_period,
            }
        }
        
        params = {
            ...params,
            frequency: {
                payroll_frequency: form.frequency,
                ...frequency,
            }
        }
        
        if (formAction === "create"){
            dispatch(payrollSetupAction.createPaySchedule(params));
        }
        else{
            dispatch(payrollSetupAction.editPaySchedule(paySchedule?.id, params));
        }
    }
    
    const isButtonDisabled = useMemo(() => {
        const { name, frequency, day_of_month, first_run_day, first_run_percent, second_run_day, second_run_percent, day_of_week, start_of_pay_period } = form;
        
        if (!name || !frequency) return true;
        
        if (frequency === "MONTHLY") {
            if (!day_of_month) return true;
        }
        
        if (frequency === "BI_MONTHLY"){
            if (!first_run_day || !first_run_percent || !second_run_day || !second_run_percent) return true;
        }
        
        if (frequency === "WEEKLY"){
            if (!day_of_week || !start_of_pay_period) return true;
        }
        
    }, [form])
    
    useEffect(() => {
        if (isOpen === true){
            let data = {...formDefault};
            if (formAction === "edit"){
                data = {
                    ...formDefault,
                    name: paySchedule?.name ?? "",
                    frequency: paySchedule?.frequency?.payroll_frequency ?? "",
                    day_of_month: paySchedule?.frequency?.day_of_month ?? "",
                    first_run_percent: paySchedule?.frequency?.first_run_percent ?? "",
                    first_run_day: paySchedule?.frequency?.first_run_day ?? "",
                    second_run_percent: paySchedule?.frequency?.second_run_percent ?? "",
                    second_run_day: paySchedule?.frequency?.second_run_day ?? "",
                    day_of_week: paySchedule?.frequency?.day_of_week ?? "",
                    start_of_pay_period: paySchedule?.frequency?.start_of_pay_period ?? "",
                };
                setForm(data);
            }
            else{
                setForm(formDefault);
            }
        }
        // eslint-disable-next-line
    }, [isOpen])
    
    useEffect(() => {
        if (payScheduleFormAction?.id !== "" && typeof payScheduleFormAction?.id === "string"){
            // dispatch(payrollSetupAction.reset());
            setForm(formDefault);
            setIsSuccess(true);
            setIsOpen(false);
            getPaySchedules();
        }
        // eslint-disable-next-line
    }, [payScheduleFormAction])
    
    return (
        <>
            <Maybe condition={isSuccess}>
                <FormDrawer display="center" size="lg" isOpen={isSuccess} setIsOpen={setIsSuccess}>
                    <div className="p-6">
                        <div className="flex justify-center">
                            <div className="h-48 sm:h-56">
                                <img src={successGif} alt="Success" className="h-full object-contain mx-auto" />
                            </div>
                        </div>

                        <div className="mt-4 py-2">
                            <div className="text-center text-[#4A5564] font-normal text-lg">
                                Payroll Schedule has been {formAction === "create" ? "created" : "edited"}
                            </div>
                        </div>

                        <div className="py-2 mt-4 flex justify-center gap-6">
                            <button type="button" className="btn btn-lg btn-block rounded-lg whitespace-nowrap text-white bg-ep-primary-600" onClick={() => {
                                setIsSuccess(false)
                            }}>
                                Close
                            </button>
                        </div>
                    </div>
                </FormDrawer>
            </Maybe>
            <FormDrawer display="center" size="lg" isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className="p-6">
                    <FormDrawer.Title
                        title={`${formAction === "create" ? "Add" : "Edit"} Pay Schedule`}
                        description="Setup a pay schedule to choose when and how often employees get paid"
                        showCloseButton={false}
                        close={setIsOpen}
                    />
                    
                    <form onSubmit={submitForm} className="mt-8">
                        <Input
                            type="text"
                            label="Schedule Name"
                            required
                            placeholder="Enter Name of Schedule"
                            value={form.name}
                            onChange={(e) => {
                                const { value } = e.target;
                                if (!REGEX_VALUES.LETTERS_WITH_NUMBERS_AND_HYPHEN_ONLY.test(value) || value.length > 50) {
                                    return;
                                }
                                setForm({...form, name: e.target.value});
                            }}
                        />
                        
                        <SelectInput label="Payroll Frequency" required={true} value={form.frequency} onChange={(e) => setForm({...form, frequency: e.target.value})}>
                            <option value="">Select</option>
                            <option value="MONTHLY">Monthly</option>
                            <option value="BI_MONTHLY">Bi-Monthly</option>
                            <option value="WEEKLY">Weekly</option>
                        </SelectInput>
                        
                        {form?.frequency === "MONTHLY" &&
                            <SelectInput label="Day of the Month" required={true} value={form.day_of_month} onChange={(e) => setForm({...form, day_of_month: e.target.value})}>
                                <option value="">Select Day</option>
                                {[...Array(32).keys()].slice(1).map((ele, index) =>
                                    <option key={index} value={ele}>{numberOrdinalSuffix(ele)}</option>
                                )}
                            </SelectInput>
                        }
                        
                        {form?.frequency === "BI_MONTHLY" &&
                            <>
                                <div className="mt-4 pt-3 border-t border-gray-100">
                                    <Input
                                        type="number"
                                        label="1st Run Percentage"
                                        required={true}
                                        min={moment().format("YYYY-MM-DD")}
                                        value={form.first_run_percent}
                                        onChange={(e) => setForm({...form, first_run_percent: e.target.value})}
                                    />
                                    
                                    <SelectInput label="1st Run Day" required={true} value={form.first_run_day} onChange={(e) => setForm({...form, first_run_day: e.target.value})}>
                                        <option value="">Select Day</option>
                                        {[...Array(32).keys()].slice(1).map((ele, index) =>
                                            <option key={index} value={ele}>{numberOrdinalSuffix(ele)}</option>
                                        )}
                                    </SelectInput>
                                </div>
                                <div className="mt-4 pt-3 border-t border-gray-100">
                                    <Input
                                        type="number"
                                        label="2nd Run Percentage"
                                        required={true}
                                        min={moment().format("YYYY-MM-DD")}
                                        value={form.second_run_percent}
                                        onChange={(e) => setForm({...form, second_run_percent: e.target.value})}
                                    />
                                    
                                    <SelectInput label="2nd Run Day" required={true} value={form.second_run_day} onChange={(e) => setForm({...form, second_run_day: e.target.value})}>
                                        <option value="">Select Day</option>
                                        {[...Array(32).keys()].slice(1).map((ele, index) =>
                                            <option key={index} value={ele}>{numberOrdinalSuffix(ele)}</option>
                                        )}
                                    </SelectInput>
                                </div>
                            </>
                        }
                        
                        {form?.frequency === "WEEKLY" &&
                            <>
                                <SelectInput label="Day of the Week" required={true} value={form.day_of_week} onChange={(e) => setForm({...form, day_of_week: e.target.value})}>
                                    <option value="">Select Day</option>
                                    {weekdays.map((day, index) =>
                                        <option key={day} value={index + 1}>{day}</option>
                                    )}
                                </SelectInput>
                                
                                <Input
                                    type="date"
                                    label="Start of pay period"
                                    required={true}
                                    min={moment().format("YYYY-MM-DD")}
                                    value={form.start_of_pay_period}
                                    onChange={(e) => setForm({...form, start_of_pay_period: e.target.value})}
                                />
                            </>
                        }
                        
                        <div className="mt-10 flex justify-end space-x-4">
                            <button type="button" onClick={() => setIsOpen(false)} className="btn btn-lg btn-transparent-primary">
                                Cancel
                            </button>
                            <ButtonLoader type="submit" loading={payScheduleFormActionLoading} className="btn btn-lg btn-ep-primary" disabled={isButtonDisabled}>
                                {formAction === "create" ? "Create" : "Edit"} Pay Schedule
                            </ButtonLoader>
                        </div>
                    </form>
                </div>
            </FormDrawer>
        </>
    )
}


export default PayScheduleForm;